import { useState } from 'react';
import { useLandmarkStore } from '../state/landmarks';
import { HISTORICAL_OVERLAYS } from '../lib/historical-overlays';

/**
 * Attribution — bottom-right. Collapsed to an "i" pill by default to keep
 * the map clear; click to reveal the full attribution popover above it.
 * Open-data licenses still satisfied as long as the info is one click away.
 */
export function Attribution() {
  const usingStadia = Boolean(import.meta.env.VITE_STADIA_API_KEY);
  const overlays = useLandmarkStore((state) => state.overlays);
  const [open, setOpen] = useState(false);

  const visibleOverlays = HISTORICAL_OVERLAYS.filter((o) => overlays[o.id].visible);

  return (
    <div
      data-testid="attribution"
      role="contentinfo"
      style={{
        position: 'absolute',
        bottom: 'var(--space-2)',
        right: 'var(--space-2)',
        zIndex: 5,
      }}
    >
      {open ? (
        <div
          data-testid="attribution-popover"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 6px)',
            right: 0,
            background: 'rgba(255, 253, 242, 0.92)',
            color: 'var(--text-muted)',
            fontSize: '0.7rem',
            padding: '6px 10px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            width: 'min(360px, calc(100vw - 16px))',
            backdropFilter: 'blur(6px)',
            lineHeight: 1.4,
            boxShadow: 'var(--shadow-card)',
            whiteSpace: 'normal',
          }}
        >
          Heritage data ©{' '}
          <a href="https://www.onroerenderfgoed.be/" target="_blank" rel="noopener noreferrer">
            Agentschap Onroerend Erfgoed
          </a>{' '}
          ·{' '}
          <a
            href="https://creativecommons.org/licenses/by/4.0/"
            target="_blank"
            rel="noopener noreferrer license"
          >
            CC-BY 4.0
          </a>
          <br />
          {usingStadia ? (
            <>
              Map tiles ©{' '}
              <a href="https://stadiamaps.com/" target="_blank" rel="noopener noreferrer">
                Stadia Maps
              </a>{' '}
              ©{' '}
              <a href="https://openmaptiles.org/" target="_blank" rel="noopener noreferrer">
                OpenMapTiles
              </a>{' '}
              ©{' '}
              <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
                OpenStreetMap
              </a>{' '}
              contributors
            </>
          ) : (
            <>
              Map tiles ©{' '}
              <a href="https://openfreemap.org/" target="_blank" rel="noopener noreferrer">
                OpenFreeMap
              </a>{' '}
              ©{' '}
              <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
                OpenStreetMap
              </a>{' '}
              contributors
            </>
          )}
          {visibleOverlays.length > 0 ? (
            <>
              <br />
              Historical maps ©{' '}
              <a href="https://www.geopunt.be/" target="_blank" rel="noopener noreferrer">
                Digitaal Vlaanderen
              </a>{' '}
              —{' '}
              <span data-testid="attribution-overlays">
                {visibleOverlays.map((o) => o.attributionTitle).join(', ')}
              </span>
            </>
          ) : null}
        </div>
      ) : null}
      <button
        type="button"
        data-testid="attribution-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={open ? 'Hide map attribution' : 'Show map attribution'}
        title="Map data attribution"
        style={{
          width: 22,
          height: 22,
          padding: 0,
          background: 'rgba(255, 253, 242, 0.88)',
          border: '1px solid var(--border)',
          borderRadius: '50%',
          color: 'var(--text-muted)',
          fontSize: '0.7rem',
          fontWeight: 700,
          cursor: 'pointer',
          backdropFilter: 'blur(6px)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
          fontFamily: 'var(--font-serif, serif)',
        }}
      >
        i
      </button>
    </div>
  );
}
